import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';
import axios from 'axios';
import webservice from '../../../services/webservice';
import { AesDecrypt } from '../../../services/crypto';
import './PeriodTrackingDashboard.css';
import { useNavigate } from 'react-router-dom';

const PeriodTrackingDashboard = () => {
    const navigate = useNavigate();
    const [kpiData, setKpiData] = useState({
        totalPeriodProfilesCount: '0',
        dailyPeriodLogsCount: '0',
        assessmentResultsCount: '0',
        avgAssessmentScore: '0',
        avgCycleLength: '0',
        avgPeriodLength: '0',
        regularCyclePercent: '0',
        regularCycleCount: 0,
        irregularCycleCount: 0,
        totalPeriodArticles: '0'
    });
    const [activityChartData, setActivityChartData] = useState();
    const [assessmentChartData, setAssessmentChartData] = useState();
    const [flowDistributionData, setFlowDistributionData] = useState([0, 0, 0, 0, 0]);
    const [cycleLengthData, setCycleLengthData] = useState([0, 0, 0, 0]);
    const [healthConditionsData, setHealthConditionsData] = useState([0, 0, 0, 0, 0, 0, 0]);
    const [assessmentTypeData, setAssessmentTypeData] = useState([0, 0, 0, 0, 0, 0, 0]);
    const [assessmentScoreData, setAssessmentScoreData] = useState([0, 0, 0, 0, 0]);
    const [trendCycleLengthCategories, setTrendCycleLengthCategories] = useState();
    const [trendCycleLengthSeriesData, setTrendCycleLengthSeriesData] = useState([0, 0, 0, 0, 0, 0]);

    useEffect(() => {
        const fetchKpis = async () => {
            try {
                const response = await axios.post(`${webservice}api/period-dashboard-kpis`);
                if (response.data && response.data.status) {
                    const decData = AesDecrypt(response.data.data);
                    setKpiData(decData);
                    if (decData.dailyUserLoggingActivity && decData.dailyUserLoggingActivity.length > 0) {
                        setActivityChartData({
                            categories: decData.dailyUserLoggingActivity.map(item => {
                                const parts = item.date.split('-');
                                if (parts.length === 3) {
                                    const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
                                    const day = parts[2];
                                    const month = dateObj.toLocaleString('en-US', { month: 'short' });
                                    const year = parts[0];
                                    return `${day} ${month} ${year}`;
                                }
                                return item.date;
                            }),
                            data: decData.dailyUserLoggingActivity.map(item => item.count)
                        });
                    }
                    if (decData.dailyAssessmentCompletion && decData.dailyAssessmentCompletion.length > 0) {
                        setAssessmentChartData({
                            categories: decData.dailyAssessmentCompletion.map(item => {
                                const parts = item.date.split('-');
                                if (parts.length === 3) {
                                    const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
                                    const day = parts[2];
                                    const month = dateObj.toLocaleString('en-US', { month: 'short' });
                                    const year = parts[0];
                                    return `${day} ${month} ${year}`;
                                }
                                return item.date;
                            }),
                            data: decData.dailyAssessmentCompletion.map(item => item.count)
                        });
                    }
                    if (decData.flowCounts) {
                        setFlowDistributionData([
                            decData.flowCounts.light || 0,
                            decData.flowCounts.medium || 0,
                            decData.flowCounts.heavy || 0,
                            decData.flowCounts['spotting only'] || 0,
                            decData.flowCounts['no bleeding'] || 0
                        ]);
                    }
                    if (decData.cycleLengthCounts) {
                        setCycleLengthData(decData.cycleLengthCounts);
                    }
                }
            } catch (err) {
                console.error('Error fetching KPIs', err);
            }
        };

        const fetchHealthConditions = async () => {
            try {
                const response = await axios.post(`${webservice}api/period-dashboard-health-conditions`);
                if (response.data && response.data.status) {
                    const decData = AesDecrypt(response.data.data);
                    if (decData.healthConditionsCounts) {
                        setHealthConditionsData([
                            decData.healthConditionsCounts.pcos || 0,
                            decData.healthConditionsCounts.endometriosis || 0,
                            decData.healthConditionsCounts.pms_pmdd || 0,
                            decData.healthConditionsCounts.thyroid || 0,
                            decData.healthConditionsCounts.fibroids || 0,
                            decData.healthConditionsCounts.diabetes || 0,
                            decData.healthConditionsCounts.none || 0
                        ]);
                    }
                }
            } catch (err) {
                console.error('Error fetching Health Conditions', err);
            }
        };
        
        const fetchAssessmentType = async () => {
            try {
                const response = await axios.post(`${webservice}api/period-dashboard-assessment-type`);
                if (response.data && response.data.status) {
                    const decData = AesDecrypt(response.data.data);
                    if (decData.assessmentTypeCounts) {
                        setAssessmentTypeData([
                            decData.assessmentTypeCounts.pcos || 0,
                            decData.assessmentTypeCounts.diabetes || 0,
                            decData.assessmentTypeCounts.fibroids || 0,
                            decData.assessmentTypeCounts.thyroid || 0,
                            decData.assessmentTypeCounts.endometriosis || 0,
                            decData.assessmentTypeCounts.pms || 0,
                            decData.assessmentTypeCounts.pms_pmdd || 0
                        ]);
                    }
                    if (decData.assessmentScoreCounts) {
                        setAssessmentScoreData(decData.assessmentScoreCounts);
                    }
                }
            } catch (err) {
                console.error('Error fetching Assessment Type', err);
            }
        };

        const fetchCycleLengthTrend = async () => {
            try {
                const response = await axios.post(`${webservice}api/period-dashboard-trend-cycle-length`);
                if (response.data && response.data.status) {
                    const decData = AesDecrypt(response.data.data);
                    if (decData.categories && decData.seriesData) {
                        setTrendCycleLengthCategories(decData.categories);
                        setTrendCycleLengthSeriesData(decData.seriesData);
                    }
                }
            } catch (err) {
                console.error('Error fetching Cycle Length Trend', err);
            }
        };

        fetchKpis();
        fetchHealthConditions();
        fetchAssessmentType();
        fetchCycleLengthTrend();
    }, []);

    // Chart Options & Data
    const activityChartOptions = {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '40%' } },
        colors: ['#3B82F6'],
        xaxis: { categories: activityChartData?.categories || [] },
        tooltip: { theme: 'light' },
        dataLabels: { enabled: false }
    };
    
    const activityChartSeries = [{ name: 'Logs', data: activityChartData?.data || [] }];

    const assessmentCompletionOptions = {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '40%' } },
        colors: ['#EC4899'],
        xaxis: { categories: assessmentChartData?.categories || [] },
        dataLabels: { enabled: false }
    };
    const assessmentCompletionSeries = [{ name: 'Completed Assessments', data: assessmentChartData?.data || [] }];

    const cycleRegularityOptions = {
        chart: { type: 'donut' },
        labels: ['Regular', 'Irregular'],
        colors: ['#14B8A6', '#EC4899'],
        plotOptions: { pie: { donut: { size: '70%' } } },
        dataLabels: { enabled: false },
        legend: { position: 'bottom' }
    };
    const cycleRegularitySeries = [kpiData.regularCycleCount || 0, kpiData.irregularCycleCount || 0];

    const flowDistributionOptions = {
        chart: { type: 'pie' },
        labels: ['Light', 'Medium', 'Heavy', 'Spotting Only', 'No Bleeding'],
        colors: ['#93C5FD', '#3B82F6', '#7C3AED', '#EC4899', '#14B8A6'],
        dataLabels: { enabled: false },
        legend: { position: 'bottom' }
    };
    const flowDistributionSeries = flowDistributionData;

    const cycleLengthOptions = {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
        colors: ['#7C3AED'],
        xaxis: { 
            categories: ['≤24', '25-28', '29-32', '33+'],
            title: { text: 'Cycle length', style: { fontWeight: 600, color: '#6e7687' } }
        },
        dataLabels: { enabled: false }
    };
    const cycleLengthSeries = [{ name: 'Users', data: cycleLengthData }];

    const healthConditionsOptions = {
        chart: { type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        colors: ['#EC4899'],
        xaxis: { categories: ['PCOS', 'Endometriosis', 'PMS / PMDD', 'Thyroid Disorder', 'Fibroids', 'Diabetes', 'None'] },
        dataLabels: { enabled: false }
    };
    const healthConditionsSeries = [{ name: 'Reports', data: healthConditionsData }];

    const assessmentTypeOptions = {
        chart: { type: 'pie' },
        labels: ['PCOS', 'Diabetes', 'Fibroids', 'Thyroid', 'Endometriosis', 'PMS', 'PMS / PMDD'],
        colors: ['#7C3AED', '#EC4899', '#3B82F6', '#14B8A6', '#F59E0B', '#EF4444', '#10B981'],
        dataLabels: { enabled: false },
        legend: { position: 'bottom' }
    };
    const assessmentTypeSeries = assessmentTypeData;

    const assessmentScoreOptions = {
        chart: { type: 'bar', toolbar: { show: false } },
        colors: ['#14B8A6'],
        xaxis: { 
            categories: ['0-20', '21-40', '41-60', '61-80', '81-100'],
            title: { text: 'Score range', style: { fontWeight: 600, color: '#6e7687' } }
        },
        plotOptions: { 
            bar: { 
                borderRadius: 4, 
                columnWidth: '40%',
                dataLabels: { position: 'top' }
            } 
        },
        dataLabels: { 
            enabled: true,
            formatter: function (val) {
                return val === 0 ? "No users" : "";
            },
            offsetY: -20,
            style: {
                fontSize: '10px',
                colors: ['#6c757d']
            }
        }
    };

    const assessmentScoreSeries = [{ name: 'Users', data: assessmentScoreData }];

    const trendCycleLengthOptions = {
        chart: { type: 'line', toolbar: { show: false }, animations: { enabled: true, easing: 'smooth' } },
        stroke: { curve: 'smooth', width: 3 },
        colors: ['#7C3AED'],
        xaxis: { categories: trendCycleLengthCategories }
    };
    const trendCycleLengthSeries = [{ name: 'Avg Cycle Length (Days)', data: trendCycleLengthSeriesData }];

    const trendScoreOptions = {
        chart: { type: 'line', toolbar: { show: false }, animations: { enabled: true, easing: 'smooth' } },
        stroke: { curve: 'smooth', width: 3 },
        colors: ['#3B82F6'],
        xaxis: { categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'] }
    };

    const trendScoreSeries = [{ name: 'Avg Score', data: [22, 23, 24, 23.5, 25, 26] }];

    return (
        <div className="period-dashboard">
            {/* Header */}
            <div className="dashboard-header d-flex justify-content-between align-items-center mb-4">
                <h2 className="dashboard-title">Period Tracking Monitor</h2>
            </div>

            <div className="row">
                {/* Main Content */}
                <div className="col-12">
                    {/* Section 1: KPI Cards */}
                    <div className="row mb-4">
                        {[
                            { title: 'Total Period Profiles', subtitle: 'Registered Users', value: kpiData.totalPeriodProfilesCount, onClick: () => { navigate('/period-profiles') }, icon: 'fa-users', color: 'purple' },
                            { title: 'Daily Period Logs', subtitle: 'Total Logs', value: kpiData.dailyPeriodLogsCount, icon: 'fa-calendar-check', color: 'blue' },
                            { title: 'Assessment Results', subtitle: 'Completed Assessments', value: kpiData.assessmentResultsCount, icon: 'fa-clipboard-list', color: 'pink' },
                            { title: 'Avg Assessment Score', subtitle: 'Overall Health Score', value: kpiData.avgAssessmentScore, icon: 'fa-heartbeat', color: 'teal' },
                            { title: 'Avg Cycle Length', subtitle: 'Days', value: kpiData.avgCycleLength, icon: 'fa-sync-alt', color: 'blue' },
                            { title: 'Avg Period Length', subtitle: 'Days', value: kpiData.avgPeriodLength, icon: 'fa-tint', color: 'pink' },
                            { title: 'Regular Cycle %', subtitle: 'Users with regular cycles', value: `${kpiData.regularCyclePercent}%`, icon: 'fa-check-circle', color: 'teal' },
                            { title: 'Period Article', subtitle: 'Total Period Articles', value: `${kpiData.totalPeriodArticles}`, onClick: () => { navigate('/OverAllPeriodTracking') }, icon: 'fa-check-circle', color: 'teal' }
                        ].map((kpi, idx) => (
                            <div className="col-xl-3 col-lg-6 col-sm-6 mb-4" key={idx}>
                                <div 
                                    className="modern-kpi-card" 
                                    onClick={kpi.onClick ? kpi.onClick : undefined}
                                    style={{ cursor: kpi.onClick ? 'pointer' : 'default' }}
                                >
                                    <div className="kpi-icon-box" data-color={kpi.color}>
                                        <i className={`fa-solid ${kpi.icon}`}></i>
                                    </div>
                                    <div className="kpi-content">
                                        <h5 className="kpi-title">{kpi.title}</h5>
                                        <p className="kpi-subtitle">{kpi.subtitle}</p>
                                        <h3 className="kpi-value">{kpi.value}</h3>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Section 2: Activity Analytics */}
                    <div className="row mb-4">
                        <div className="col-lg-6 mb-4 mb-lg-0">
                            <div className="modern-card">
                                <h4 className="card-title">Daily User Logging Activity</h4>
                                <ReactApexChart options={activityChartOptions} series={activityChartSeries} type="bar" height={350} />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="modern-card">
                                <h4 className="card-title">Daily Assessment Completion</h4>
                                <ReactApexChart options={assessmentCompletionOptions} series={assessmentCompletionSeries} type="bar" height={350} />
                            </div>
                        </div>
                    </div>

                    {/* Section 3: Period Health Analytics */}
                    <div className="row mb-4">
                        <div className="col-lg-4 mb-4 mb-lg-0">
                            <div className="modern-card">
                                <h4 className="card-title">Cycle Regularity</h4>
                                {cycleRegularitySeries.reduce((a, b) => a + (b || 0), 0) > 0 ? (
                                    <ReactApexChart options={cycleRegularityOptions} series={cycleRegularitySeries} type="donut" height={300} />
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center text-muted" style={{ height: '300px' }}>
                                        No Data Available
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-lg-4 mb-4 mb-lg-0">
                            
                             <div className="modern-card">
                                <h4 className="card-title">Cycle Length Distribution</h4>
                                {cycleLengthSeries[0].data.reduce((a, b) => a + (b || 0), 0) > 0 ? (
                                    <ReactApexChart options={cycleLengthOptions} series={cycleLengthSeries} type="bar" height={300} />
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center text-muted" style={{ height: '300px' }}>
                                        No Data Available
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="modern-card">
                                <h4 className="card-title">Menstrual Flow Distribution</h4>
                                {flowDistributionSeries.reduce((a, b) => a + (b || 0), 0) > 0 ? (
                                    <ReactApexChart options={flowDistributionOptions} series={flowDistributionSeries} type="pie" height={300} />
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center text-muted" style={{ height: '300px' }}>
                                        No Data Available
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Section 4: Health Conditions & Trends */}
                    <div className="row mb-4">
                        <div className="col-lg-6 mb-4 mb-lg-0">
                             <div className="modern-card" style={{ height: '100%' }}>
                                <h4 className="card-title">Average Cycle Length Trend</h4>
                                <ReactApexChart options={trendCycleLengthOptions} series={trendCycleLengthSeries} type="line" height={350} />
                            </div>
                            
                        </div>
                        <div className="col-lg-6">
                            <div className="modern-card">
                                <h4 className="card-title">Most Reported Health Conditions</h4>
                                {healthConditionsSeries[0].data.reduce((a, b) => a + (b || 0), 0) > 0 ? (
                                    <ReactApexChart options={healthConditionsOptions} series={healthConditionsSeries} type="bar" height={350} />
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center text-muted" style={{ height: '350px' }}>
                                        No Data Available
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Section 5: Assessment Analytics */}
                    <div className="row mb-4">
                        <div className="col-lg-6 mb-4 mb-lg-0">
                            <div className="modern-card">
                                <h4 className="card-title">Assessment Type Distribution</h4>
                                {assessmentTypeSeries.reduce((a, b) => a + (b || 0), 0) > 0 ? (
                                    <ReactApexChart options={assessmentTypeOptions} series={assessmentTypeSeries} type="pie" height={300} />
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center text-muted" style={{ height: '300px' }}>
                                        No Data Available
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="modern-card">
                                <h4 className="card-title">Assessment Score Distribution</h4>
                                {assessmentScoreSeries[0].data.reduce((a, b) => a + (b || 0), 0) > 0 ? (
                                    <ReactApexChart options={assessmentScoreOptions} series={assessmentScoreSeries} type="bar" height={300} />
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center text-muted" style={{ height: '300px' }}>
                                        No Data Available
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PeriodTrackingDashboard;
